import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTimes, FaPlus, FaCreditCard, FaCheck,
  FaGift, FaPercent, FaStar, FaEye, FaTools, FaHeart, FaShieldAlt, FaBolt,
} from 'react-icons/fa';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import api from '../../../services/api';

// Icon mapping
const ICONS = {
  gift: FaGift,
  percent: FaPercent,
  sparkles: FaStar,
  eye: FaEye,
  tool: FaTools,
  heart: FaHeart,
  shield: FaShieldAlt,
  zap: FaBolt,
};

const ICON_OPTIONS = [
  { key: 'gift', label: 'Cadeau' },
  { key: 'percent', label: 'Remise' },
  { key: 'sparkles', label: 'Étoile' },
  { key: 'eye', label: 'Vision' },
  { key: 'tool', label: 'Réglage' },
  { key: 'heart', label: 'Coup de coeur' },
  { key: 'shield', label: 'Garantie' },
  { key: 'zap', label: 'Express' },
];

// Quick-start templates: clickable presets to seed the form. The pro can
// tweak the title/description after picking one.
const TEMPLATES = [
  { icon: 'percent', title: '-20% sur la 2e paire', description: 'Sur toute la collection montures' },
  { icon: 'eye', title: 'Bilan visuel offert', description: 'Pour vous et votre famille' },
  { icon: 'tool', title: 'Ajustement gratuit à vie', description: 'Sur tous vos achats' },
  { icon: 'shield', title: 'Garantie 2 ans', description: 'Casse, rayures, perte' },
];

const CardAdvantagesPage = () => {
  const { brand, brandName, brandLogo, currentUser } = useProContext();

  const [advantages, setAdvantages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newAdvantage, setNewAdvantage] = useState({ title: '', description: '', icon: 'gift' });

  const fetchAdvantages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await api.get('/stores/card/advantages', { headers: { Authorization: `Bearer ${token}` } });
      setAdvantages(Array.isArray(data) ? data : []);
    } catch { setAdvantages([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdvantages(); }, [fetchAdvantages]);

  const addAdvantage = async () => {
    if (!newAdvantage.title.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await api.post('/stores/card/advantages', newAdvantage, { headers: { Authorization: `Bearer ${token}` } });
      setNewAdvantage({ title: '', description: '', icon: 'gift' });
      setShowForm(false);
      await fetchAdvantages();
    } catch (e) { console.error(e); }
  };

  const deleteAdvantage = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/stores/card/advantages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchAdvantages();
    } catch (e) { console.error(e); }
  };

  const cardNumber = `FDZ-${String(currentUser?.id || 0).padStart(6, '0')}`;
  const userSince = new Date(currentUser?.created_at || Date.now()).getFullYear();

  return (
    <ProLayout title="Carte avantage">
      <div className="p-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ═══ LEFT: Card preview (like mobile app) ═══ */}
          <div className="space-y-4">
            <div>
              <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-1">Aperçu</p>
              <h3 className="text-sm font-bold text-gray-900">Ce que voient vos clients</h3>
            </div>

            {/* The Card */}
            <div className="relative rounded-3xl overflow-hidden p-6 text-white" style={{ backgroundColor: brand, minHeight: 220 }}>
              {/* Decorative circles */}
              <div className="absolute rounded-full bg-white/10" style={{ width: 200, height: 200, top: -60, right: -40 }} />
              <div className="absolute rounded-full bg-white/5" style={{ width: 150, height: 150, bottom: -40, left: -30 }} />

              {/* Content */}
              <div className="relative flex flex-col h-full justify-between" style={{ minHeight: 188 }}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[0.625rem] font-semibold tracking-[2px] text-white/60 mb-1">CARTE AVANTAGE</p>
                    <h2 className="text-lg font-bold">{brandName || 'Votre boutique'}</h2>
                  </div>
                  <FaCreditCard className="w-6 h-6 text-white/40" />
                </div>

                <div className="mt-5">
                  <p className="text-xl font-bold">Nom du client</p>
                  <p className="text-[0.6875rem] text-white/50 mt-0.5">Membre depuis {userSince}</p>
                </div>

                <p className="text-[0.75rem] text-white/40 tracking-widest text-right mt-4">{cardNumber}</p>
              </div>
            </div>

            {/* Advantages preview (like mobile app) */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <h4 className="text-sm font-bold text-gray-900">Vos avantages</h4>
              </div>
              {advantages.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-[0.8125rem] text-gray-300">Aucun avantage configuré</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {advantages.map(a => {
                    const Icon = ICONS[a.icon] || FaGift;
                    return (
                      <div key={a.id} className="flex items-center gap-4 px-5 py-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: brand + '12' }}>
                          <Icon className="w-4 h-4" style={{ color: brand }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[0.8125rem] font-semibold text-gray-900">{a.title}</p>
                          {a.description && <p className="text-[0.6875rem] text-gray-500 mt-0.5">{a.description}</p>}
                        </div>
                        <FaCheck className="w-3.5 h-3.5" style={{ color: brand }} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ═══ RIGHT: Management ═══ */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-1">Gestion</p>
                <h3 className="text-sm font-bold text-gray-900">
                  {advantages.length} avantage{advantages.length > 1 ? 's' : ''} configuré{advantages.length > 1 ? 's' : ''}
                </h3>
              </div>
              <button
                onClick={() => setShowForm(!showForm)}
                className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-colors"
                style={{ backgroundColor: brand }}
              >
                {showForm ? <FaTimes className="w-3 h-3" /> : <FaPlus className="w-3 h-3" />}
                {showForm ? 'Annuler' : 'Nouvel avantage'}
              </button>
            </div>

            {/* Form */}
            {showForm && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                {/* Templates */}
                <div>
                  <p className="text-[0.6875rem] font-semibold text-gray-500 mb-2">Modèles rapides</p>
                  <div className="grid grid-cols-2 gap-2">
                    {TEMPLATES.map((t, i) => {
                      const Icon = ICONS[t.icon];
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setNewAdvantage({ icon: t.icon, title: t.title, description: t.description })}
                          className="flex items-start gap-2.5 p-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: brand + '12' }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: brand }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[0.75rem] font-semibold text-gray-900 leading-tight truncate">{t.title}</p>
                            <p className="text-[0.625rem] text-gray-400 leading-tight mt-0.5 line-clamp-2">{t.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[0.625rem] text-gray-400 mt-2">Cliquez pour pré-remplir, modifiez ensuite à votre convenance</p>
                </div>

                <div className="border-t border-gray-100" />

                {/* Icon selector */}
                <div>
                  <p className="text-[0.6875rem] font-semibold text-gray-500 mb-2">Icône</p>
                  <div className="grid grid-cols-4 gap-2">
                    {ICON_OPTIONS.map(ic => {
                      const Icon = ICONS[ic.key];
                      const isSelected = newAdvantage.icon === ic.key;
                      return (
                        <button
                          key={ic.key}
                          onClick={() => setNewAdvantage(f => ({ ...f, icon: ic.key }))}
                          className="flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all"
                          style={isSelected
                            ? { backgroundColor: brand + '10', borderColor: brand }
                            : { backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }
                          }
                        >
                          <Icon className="w-4 h-4" style={{ color: isSelected ? brand : '#9CA3AF' }} />
                          <span className="text-[0.625rem] font-medium" style={{ color: isSelected ? brand : '#6B7280' }}>{ic.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-[0.6875rem] font-semibold text-gray-500 mb-1.5">Titre</p>
                  <input
                    value={newAdvantage.title}
                    onChange={e => setNewAdvantage(f => ({ ...f, title: e.target.value }))}
                    placeholder="-20% sur la 2e paire"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <div>
                  <p className="text-[0.6875rem] font-semibold text-gray-500 mb-1.5">Description (optionnel)</p>
                  <input
                    value={newAdvantage.description}
                    onChange={e => setNewAdvantage(f => ({ ...f, description: e.target.value }))}
                    placeholder="Sur toute la collection montures"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <button
                  onClick={addAdvantage}
                  disabled={!newAdvantage.title.trim()}
                  className="w-full py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-40"
                  style={{ backgroundColor: brand }}
                >
                  Ajouter l'avantage
                </button>
              </div>
            )}

            {/* List of advantages */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-gray-300">Chargement...</p>
                </div>
              ) : advantages.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: brand + '10' }}>
                    <FaGift className="w-5 h-5" style={{ color: brand }} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Aucun avantage</p>
                  <p className="text-[0.75rem] text-gray-400">Ajoutez des avantages pour votre carte client</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {advantages.map(a => {
                    const Icon = ICONS[a.icon] || FaGift;
                    return (
                      <div key={a.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: brand + '12' }}>
                          <Icon className="w-4 h-4" style={{ color: brand }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{a.title}</p>
                          {a.description && <p className="text-[0.6875rem] text-gray-500">{a.description}</p>}
                        </div>
                        <button
                          onClick={() => deleteAdvantage(a.id)}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          title="Supprimer"
                        >
                          <FaTimes className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProLayout>
  );
};

export default CardAdvantagesPage;
